/**
 * <pi-product-addons>
 * Checkbox add-ons beside Instant variant pickers.
 * Styles Instant ATC, updates total with checked add-ons + selected variant, mobile sticky ATC.
 */
(() => {
  if (customElements.get('pi-product-addons')) return;

  const MONEY_PLACEHOLDERS = [
    'amount_no_decimals_with_comma_separator',
    'amount_with_comma_separator',
    'amount_no_decimals',
    'amount',
  ];

  class PiProductAddons extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.root =
        this.closest('[data-instant-type="root"]') ||
        document.querySelector('.__instant [data-instant-type="root"]');

      this.addonInputs = Array.from(this.querySelectorAll('[data-pi-addon-input]'));
      const prices = this._readVariantPrices();
      this.variantPricesById = prices.byId;
      this.variantPricesByOption = prices.byOption;
      this._pendingOptionValue = '';
      this._pendingVariantId = 0;

      this.currency =
        this.dataset.currency || window.Shopify?.currency?.active || 'USD';
      this.moneyFormat =
        this.dataset.moneyFormat ||
        window.themeVariables?.settings?.moneyFormat ||
        '${{amount}}';
      this.moneyWithCurrencyFormat =
        this.dataset.moneyWithCurrencyFormat ||
        window.themeVariables?.settings?.moneyWithCurrencyFormat ||
        this.moneyFormat;

      this._onAddonPointer = this._onAddonPointer.bind(this);
      this._onAddonClickGuard = this._onAddonClickGuard.bind(this);
      this._onAtcPointer = this._onAtcPointer.bind(this);
      this._onAddonChange = this._onAddonChange.bind(this);
      this._onAddonKeydown = this._onAddonKeydown.bind(this);
      this._onVariantPointer = this._onVariantPointer.bind(this);

      document.addEventListener('click', this._onAddonPointer, true);
      document.addEventListener('click', this._onAddonClickGuard, true);
      document.addEventListener('click', this._onAtcPointer, true);
      document.addEventListener('click', this._onVariantPointer, true);
      this.addonInputs.forEach((input) => input.addEventListener('change', this._onAddonChange));
      this.querySelectorAll('[data-pi-addon-card]').forEach((card) => {
        card.addEventListener('keydown', this._onAddonKeydown);
      });

      this.prepareAtcButtons();
      this.setupStickyAtc();
      this._watchVariantChanges();
      this.updatePriceDisplays();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onAddonPointer, true);
      document.removeEventListener('click', this._onAddonClickGuard, true);
      document.removeEventListener('click', this._onAtcPointer, true);
      document.removeEventListener('click', this._onVariantPointer, true);
      this.addonInputs?.forEach((input) => input.removeEventListener('change', this._onAddonChange));
      this.querySelectorAll('[data-pi-addon-card]').forEach((card) => {
        card.removeEventListener('keydown', this._onAddonKeydown);
      });
      if (this._variantObserver) {
        this._variantObserver.disconnect();
        this._variantObserver = null;
      }
      if (this._stickyObserver) {
        this._stickyObserver.disconnect();
        this._stickyObserver = null;
      }
      if (this._stickyBar) {
        this._stickyBar.remove();
        this._stickyBar = null;
        this._stickyBtn = null;
      }
      clearTimeout(this._priceRetryTimer);
      clearTimeout(this._variantRefreshTimer);
    }

    _normalizeOptionKey(value) {
      return String(value || '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    _readVariantPrices() {
      const el = this.querySelector('[data-pi-variant-prices]');
      const empty = { byId: {}, byOption: {} };
      if (!el) return empty;
      try {
        const raw = JSON.parse(el.textContent) || {};
        // Back-compat: flat { "id": price }
        if (!raw.byId && !raw.byOption) {
          return { byId: raw, byOption: {} };
        }
        const byOption = {};
        Object.entries(raw.byOption || {}).forEach(([key, value]) => {
          byOption[this._normalizeOptionKey(key)] = {
            id: Number(value?.id) || 0,
            price: Number(value?.price) || 0,
          };
        });
        return {
          byId: raw.byId || {},
          byOption,
        };
      } catch (error) {
        console.error('[pi-product-addons] invalid variant prices', error);
        return empty;
      }
    }

    get selectedAddons() {
      return this.addonInputs
        .filter((input) => input.checked && !input.disabled)
        .map((input) => ({
          id: Number(input.dataset.variantId),
          price: Number(input.dataset.price) || 0,
          quantity: 1,
        }))
        .filter((item) => item.id);
    }

    get atcButtons() {
      if (!this.root) return [];
      return Array.from(
        this.root.querySelectorAll(
          '[data-pi-atc], [data-instant-action-type="add-to-instant-cart"], .iYpkkmkTGz5ZBUq3E'
        )
      );
    }

    get activeOptionValue() {
      if (this._pendingOptionValue) return this._pendingOptionValue;

      const active = this.root?.querySelector(
        '[data-instant-action-type="select-variant-option"][data-instant-state="active"], .instant-custom-variant-picker[data-instant-state="active"]'
      );
      return this._normalizeOptionKey(active?.getAttribute('data-instant-option-value'));
    }

    get currentVariantId() {
      const fromOption = this.variantPricesByOption[this.activeOptionValue];
      if (fromOption?.id) return fromOption.id;
      if (this._pendingVariantId) return this._pendingVariantId;

      const fromForm = Number(this.root?.getAttribute('data-instant-form-variant-id'));
      if (fromForm) return fromForm;
      const fromAtc = Number(
        this.atcButtons[0]?.getAttribute('data-instant-action-variant-id')
      );
      return fromAtc || 0;
    }

    get variantPrice() {
      const fromOption = this.variantPricesByOption[this.activeOptionValue];
      if (fromOption?.price > 0) return fromOption.price;

      const id = this.currentVariantId;
      const mapped = Number(this.variantPricesById[id] || this.variantPricesById[String(id)]);
      if (mapped > 0) return mapped;
      return Number(this._fallbackVariantPrice) || 0;
    }

    get addonsPrice() {
      return this.selectedAddons.reduce((sum, item) => sum + Number(item.price || 0), 0);
    }

    get totalPrice() {
      return this.variantPrice + this.addonsPrice;
    }

    formatMoney(cents, { withCurrency = false } = {}) {
      const amount = Number(cents) / 100;

      try {
        const formatted = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currency,
          currencyDisplay: 'symbol',
        }).format(amount);

        if (withCurrency) {
          return formatted.includes(this.currency) ? formatted : `${formatted} ${this.currency}`;
        }
        return formatted;
      } catch (error) {
        // fall through
      }

      const format = withCurrency ? this.moneyWithCurrencyFormat : this.moneyFormat;
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, format);
      }

      const value = amount.toFixed(2);
      const amountNoDecimals = Math.round(amount).toString();
      const amountWithComma = value.replace('.', ',');
      const amountNoDecimalsWithComma = amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      const map = {
        amount_no_decimals_with_comma_separator: amountNoDecimalsWithComma,
        amount_with_comma_separator: amountWithComma,
        amount_no_decimals: amountNoDecimals,
        amount: value,
      };

      let output = format || '${{amount}}';
      MONEY_PLACEHOLDERS.forEach((key) => {
        output = output.split(`{{${key}}}`).join(map[key]).split(`{{ ${key} }}`).join(map[key]);
      });

      const tmp = document.createElement('div');
      tmp.innerHTML = output;
      const text = tmp.textContent || tmp.innerText || output;
      if (withCurrency && !text.includes(this.currency)) return `${text} ${this.currency}`;
      return text;
    }

    prepareAtcButtons() {
      this.atcButtons.forEach((atc) => {
        atc.classList.add('pi-atc');
        atc.removeAttribute('data-instant-disabled');
        atc.setAttribute('data-pi-atc', '');

        const priceWrap = atc.querySelector('[data-atc-price], .iPoKvsxqLx1Rk95vx, .pi-atc__price');
        let priceEl = atc.querySelector('[data-pi-atc-price], .pi-atc__price-value');

        atc.querySelectorAll('[data-instant-dynamic-content-source="PRICE"]').forEach((el) => {
          if (!this._fallbackVariantPrice) {
            const parsed = this._parseMoneyToCents(el.textContent);
            if (parsed > 0) this._fallbackVariantPrice = parsed;
          }
          el.removeAttribute('data-instant-dynamic-content-source');
          el.removeAttribute('data-instant-type');
          el.setAttribute('data-pi-atc-price', '');
          el.classList.add('pi-atc__price-value');
          priceEl = el;
        });

        if (!atc.querySelector('.pi-atc__label')) {
          const label = document.createElement('span');
          label.className = 'pi-atc__label';
          label.textContent = 'ADD TO CART';
          atc.insertBefore(label, atc.firstChild);
        }

        // Hide Instant compare only; icon / label / dot stay for desktop Instant look
        atc
          .querySelectorAll(
            '[data-instant-dynamic-content-source="COMPARE_AT"], .inN8XIfYniDqVyyuq'
          )
          .forEach((el) => {
            el.style.display = 'none';
          });

        if (priceWrap) {
          priceWrap.classList.add('pi-atc__price', 'iPoKvsxqLx1Rk95vx');
          priceWrap.setAttribute('data-atc-price', '');
          priceWrap.setAttribute('data-instant-styles', 'none');
        }
        if (priceEl) {
          priceEl.setAttribute('data-pi-atc-price', '');
          priceEl.classList.add('pi-atc__price-value');
        } else if (priceWrap) {
          const created = document.createElement('span');
          created.className = 'pi-atc__price-value';
          created.setAttribute('data-pi-atc-price', '');
          priceWrap.appendChild(created);
        }
      });
    }

    setupStickyAtc() {
      if (this._stickyBar) return;

      const sourceAtc = this.atcButtons[0];
      if (!sourceAtc) return;

      const bar = document.createElement('div');
      bar.className = 'pi-sticky-atc';
      bar.setAttribute('data-pi-sticky-atc', '');
      bar.setAttribute('aria-hidden', 'true');
      bar.innerHTML = `
        <div class="pi-sticky-atc__inner">
          <button type="button" class="pi-atc pi-sticky-atc__btn" data-pi-sticky-atc-btn>
            <span class="pi-atc__label">ADD TO CART</span>
            <span class="pi-atc__price" data-atc-price>
              <span class="pi-atc__price-value" data-pi-atc-price data-pi-sticky-atc-price></span>
            </span>
          </button>
        </div>
      `;
      document.body.appendChild(bar);

      const stickyBtn = bar.querySelector('[data-pi-sticky-atc-btn]');
      stickyBtn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.addWithAddons(stickyBtn);
      });

      this._stickyBar = bar;
      this._stickyBtn = stickyBtn;

      const setStickyVisible = (visible) => {
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      };

      this._stickyObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          setStickyVisible(!entry.isIntersecting);
        },
        { root: null, threshold: 0, rootMargin: '0px' }
      );
      this._stickyObserver.observe(sourceAtc);

      requestAnimationFrame(() => {
        const rect = sourceAtc.getBoundingClientRect();
        const inView = rect.bottom > 0 && rect.top < window.innerHeight;
        setStickyVisible(!inView);
        this.updatePriceDisplays({ fromObserver: true });
      });
    }

    _schedulePriceRefresh() {
      clearTimeout(this._variantRefreshTimer);
      const delays = [0, 40, 120, 250, 500];
      delays.forEach((delay) => {
        setTimeout(() => {
          // Clear pending once Instant form id matches selected option
          const formId = Number(this.root?.getAttribute('data-instant-form-variant-id'));
          if (this._pendingVariantId && formId && formId === this._pendingVariantId) {
            this._pendingOptionValue = '';
            this._pendingVariantId = 0;
          }
          this.updatePriceDisplays({ fromObserver: true });
        }, delay);
      });
    }

    _onVariantPointer(event) {
      const picker = event.target?.closest?.(
        '[data-instant-action-type="select-variant-option"], .instant-custom-variant-picker'
      );
      if (!picker || !this.root?.contains(picker)) return;

      const optionValue = this._normalizeOptionKey(
        picker.getAttribute('data-instant-option-value')
      );
      if (optionValue) {
        this._pendingOptionValue = optionValue;
        const mapped = this.variantPricesByOption[optionValue];
        if (mapped?.id) this._pendingVariantId = mapped.id;
      }

      this._schedulePriceRefresh();
    }

    _watchVariantChanges() {
      if (!this.root || this._variantObserver) return;

      this._variantObserver = new MutationObserver(() => {
        if (this._applyingPrices || this._priceUpdateQueued) return;
        this._priceUpdateQueued = true;
        requestAnimationFrame(() => {
          this._priceUpdateQueued = false;
          this.updatePriceDisplays({ fromObserver: true });
        });
      });

      this._variantObserver.observe(this.root, {
        attributes: true,
        attributeFilter: ['data-instant-form-variant-id'],
      });

      this.atcButtons.forEach((atc) => {
        this._variantObserver.observe(atc, {
          attributes: true,
          attributeFilter: ['data-instant-action-variant-id'],
        });
      });

      this.root
        .querySelectorAll(
          '[data-instant-action-type="select-variant-option"], .instant-custom-variant-picker'
        )
        .forEach((el) => {
          this._variantObserver.observe(el, {
            attributes: true,
            attributeFilter: ['data-instant-state', 'data-instant-option-value'],
          });
        });

      this.root
        .querySelectorAll('[data-instant-dynamic-content-source="PRICE"]')
        .forEach((el) => {
          if (el.closest('.pi-atc, [data-pi-atc], [data-atc-price]')) return;
          this._variantObserver.observe(el, {
            subtree: true,
            characterData: true,
            childList: true,
          });
        });
    }

    _parseMoneyToCents(text) {
      if (!text) return 0;
      const cleaned = String(text).replace(/[^\d.,-]/g, '').trim();
      if (!cleaned) return 0;
      const normalized =
        cleaned.includes(',') && cleaned.includes('.')
          ? cleaned.replace(/,/g, '')
          : cleaned.includes(',')
            ? cleaned.replace(',', '.')
            : cleaned;
      const amount = Number.parseFloat(normalized);
      if (!Number.isFinite(amount)) return 0;
      return Math.round(amount * 100);
    }

    updatePriceDisplays({ fromObserver = false } = {}) {
      if (!this.root) return;
      if (this._applyingPrices) return;
      this._applyingPrices = true;

      try {
        const id = this.currentVariantId;
        const hasMapped =
          Number(this.variantPricesById[id] || this.variantPricesById[String(id)]) > 0 ||
          Number(this.variantPricesByOption[this.activeOptionValue]?.price) > 0;

        if (!hasMapped) {
          this.root.querySelectorAll('[data-instant-dynamic-content-source="PRICE"]').forEach((el) => {
            if (el.closest('.pi-atc, [data-pi-atc], [data-atc-price]')) return;
            const parsed = this._parseMoneyToCents(el.textContent);
            if (parsed > 0) this._fallbackVariantPrice = parsed;
          });
        }

        const total = this.totalPrice;
        const atcPriceText = this.formatMoney(total, { withCurrency: true });

        this.root.querySelectorAll('[data-pi-atc-price], .pi-atc__price-value').forEach((el) => {
          if (el.textContent !== atcPriceText) el.textContent = atcPriceText;
        });

        document.querySelectorAll('[data-pi-sticky-atc-price]').forEach((el) => {
          if (el.textContent !== atcPriceText) el.textContent = atcPriceText;
        });

        if (!fromObserver) {
          clearTimeout(this._priceRetryTimer);
          this._priceRetryTimer = setTimeout(() => {
            this.updatePriceDisplays({ fromObserver: true });
          }, 50);
        }
      } finally {
        this._applyingPrices = false;
      }
    }

    _onAddonPointer(event) {
      const card = event.target?.closest?.('[data-pi-addon-card]');
      if (!card || !this.contains(card)) return;
      if (event.target.closest('[data-pi-drawer-open], a, button.pi-addons__link')) return;
      if (event.target.matches('input[type="checkbox"]')) return;
      event.preventDefault();
      event.stopPropagation();
      this._toggleCard(card);
    }

    _onAddonClickGuard(event) {
      const card = event.target?.closest?.('[data-pi-addon-card]');
      if (!card || !this.contains(card)) return;
      if (event.target.closest('[data-pi-drawer-open], a, button.pi-addons__link')) return;
    }

    _onAddonKeydown(event) {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      const card = event.currentTarget;
      if (!card || event.target?.closest?.('a, [data-pi-drawer-open]')) return;
      event.preventDefault();
      this._toggleCard(card);
    }

    _onAddonChange(event) {
      const input = event.target;
      const card = input?.closest?.('[data-pi-addon-card]');
      if (!card || !this.contains(card)) return;
      card.setAttribute('aria-checked', input.checked ? 'true' : 'false');
      card.classList.toggle('is-checked', input.checked);
      this.updatePriceDisplays();
    }

    _toggleCard(card) {
      const input = card.querySelector('[data-pi-addon-input]');
      if (!input || input.disabled) return;
      input.checked = !input.checked;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    _onAtcPointer(event) {
      const atc = event.target?.closest?.(
        '[data-instant-action-type="add-to-instant-cart"], [data-pi-atc], .iYpkkmkTGz5ZBUq3E'
      );
      if (!atc || !this.root?.contains(atc)) return;
      if (!this.selectedAddons.length) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      this.addWithAddons(atc);
    }

    async postCartAdd(items) {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error(`cart/add failed: ${res.status}`);
      return res.json();
    }

    openInstantCart() {
      const openBtn =
        document.querySelector('[data-instant-action-type="open-instant-cart"]') ||
        document.querySelector('[data-instant-layout="CART"] [data-instant-action-type="open"]');
      if (openBtn) {
        openBtn.click();
        return;
      }
      document.dispatchEvent(new CustomEvent('instant:cart:open'));
      window.Instant?.cart?.open?.();
    }

    async addWithAddons(atc) {
      if (this._busy) return;
      this._busy = true;
      atc?.setAttribute?.('aria-busy', 'true');
      this._stickyBtn?.setAttribute('aria-busy', 'true');

      try {
        const variantId = Number(
          this.currentVariantId ||
            atc?.getAttribute?.('data-instant-action-variant-id') ||
            this.root?.querySelector('[data-instant-action-variant-id]')?.getAttribute(
              'data-instant-action-variant-id'
            )
        );
        if (!variantId) return;

        const items = [
          { id: variantId, quantity: 1 },
          ...this.selectedAddons.map((a) => ({
            id: a.id,
            quantity: a.quantity || 1,
            properties: { _pi_bundle_role: 'addon' },
          })),
        ];

        await this.postCartAdd(items);
        this.openInstantCart();
      } catch (error) {
        console.error('[pi-product-addons] add failed', error);
      } finally {
        atc?.removeAttribute?.('aria-busy');
        this._stickyBtn?.removeAttribute('aria-busy');
        this._busy = false;
      }
    }
  }

  customElements.define('pi-product-addons', PiProductAddons);
})();
